import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  isShippingRestrictedForParent,
  isCategoryRestricted as computeCategoryRestricted,
} from '../helpers/internationalRestriction';

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  padding-top: 50%; // 1:2 Aspect Ratio

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    object-fit: contain;
  }
`;

const Badge = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 3;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1;
  white-space: nowrap;
`;

const RestrictedBadge = styled(Badge)`
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffeeba;
`;

const RestrictedWrap = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  opacity: 0.55;
  pointer-events: none;
  cursor: not-allowed;
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: flex-end;
  align-items: center;
  margin: 0 0 20px;
`;

const actionButtonBase = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.1s ease, color 0.1s ease;
  min-width: 200px;
  box-sizing: border-box;

  @media (max-width: 767px) {
    width: 100%;
    min-width: 0;
  }
`;

const OutlineActionLink = styled(Link)`
  ${actionButtonBase}
  background: #ffffff;
  color: #479de6;
  border: 1px solid #479de6;

  &:hover,
  &:focus {
    background: #f3f8fd;
    color: #357ab8;
    text-decoration: none;
  }
`;

const PrimaryActionLink = styled(Link)`
  ${actionButtonBase}
  background: #479de6;
  color: #ffffff;
  border: 1px solid #479de6;

  &:hover,
  &:focus {
    background: #357ab8;
    color: #ffffff;
    text-decoration: none;
  }
`;

const Offerings = () => {
  const orders = useSelector((state) => state.v3Order.orders);
  const selectedStudent = useSelector(
    (state) => state.additionalData.selectedStudent,
  );
  const shootEventId = useSelector((state) => {
    const s = state.shoot.shoot;
    return s?.event_id || s?.id;
  });
  const company = useSelector((state) => state.company);
  const parent = useSelector((state) => state.parent);
  const shoot = useSelector((state) => state.shoot.shoot);
  const restricted = isShippingRestrictedForParent(shoot, parent);

  // Get all products from the event -- categories are the top-level products
  const shootProducts = useSelector((state) => state.shoot.shootProducts);

  // Filter out categories that should not be displayed.
  // When `restricted`, fully-restricted categories stay visible but are
  // rendered as non-clickable with a "Not available internationally" badge
  // (see render below). Hiding them caused the bouncing-redirect UX issue.
  const categories = shootProducts.filter((category) => {
    if (category.parent_category_id !== null) return false;
    if (category.category_type === 'nth_free') return false;
    if (category.is_visible !== true) return false;
    return true;
  });

  const isCategoryRestricted = (category) =>
    computeCategoryRestricted(category, shootProducts, restricted);

  const maxIndex = categories.length - 1;

  const renderActionRow = () => {
    const orderKey =
      shootEventId && selectedStudent?.id
        ? `${shootEventId}-${selectedStudent.id}`
        : null;
    const cartItems =
      (orderKey ? orders[orderKey] : undefined)?.cartItems || [];
    const showCheckout = cartItems.length > 0;
    const orderForAnotherText =
      company?.name === 'sportsjournal'
        ? 'Order For Another Player'
        : 'Order For Another Student';

    return (
      <ActionRow>
        <OutlineActionLink to="/dashboard-redirect">
          {orderForAnotherText}
        </OutlineActionLink>
        {showCheckout && (
          <PrimaryActionLink to="/v3/cart">Check Out Now</PrimaryActionLink>
        )}
      </ActionRow>
    );
  };

  const offerings = categories.map((category, index) => {
    const categoryRestricted = isCategoryRestricted(category);
    const baseClass = categoryRestricted ? '' : 'cursor-pointer';
    let className = `bnl-product-group bnl-product-group--small col-xs-12 col-md-6 col-lg-6 ${baseClass}`;

    if (index === 0) {
      className = `bnl-product-group bnl-product-group--top col-xs-12 col-md-12 col-lg-12 ${baseClass}`;
    } else if (
      index === maxIndex &&
      index !== 2 &&
      index % 2 !== 0 &&
      category.display_type !== 'small'
    ) {
      className = `bnl-product-group bnl-product-group--bottom col-xs-12 col-md-12 col-lg-12 ${baseClass}`;
    }

    // When restricted, suppress the Link (no click), dim the card, and show
    // the "Not available internationally" badge. When the parent is non-US
    // but this category is OK, show an affirmative "Available" badge.
    const cardBody = (
      <>
        {categoryRestricted ? null : category?.products?.length === 1 ? (
          <Link
            to={`/v3/products-regular/${category.products[0].id}`}
            className="categoryLink"
          />
        ) : (
          <Link to={`/v3/categories/${category.id}`} className="categoryLink" />
        )}
        <h3 className="bnl-product-group__header">{category.name}</h3>
        <div className="bnl-product-group__subheader">{category.subheader}</div>
        <div className="bnl-product-group__image-container">
          <ImageContainer>
            <img
              className="bnl-product-group__image--single"
              src={category.image_url}
            />
          </ImageContainer>
        </div>
      </>
    );

    return (
      <li key={category.id} className={className}>
        {restricted && categoryRestricted && (
          <RestrictedBadge>Not available internationally</RestrictedBadge>
        )}
        {categoryRestricted ? (
          <RestrictedWrap>{cardBody}</RestrictedWrap>
        ) : (
          cardBody
        )}
      </li>
    );
  });

  return (
    <div className="bnl-product-groups-container">
      {renderActionRow()}
      <ul className="bnl-product-groups row">{offerings}</ul>
    </div>
  );
};

export default Offerings;
